'use client'

import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { LenisRef } from 'lenis/react'
import { ReactLenis } from 'lenis/react'
import { cancelFrame, frame } from 'motion'
import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'

gsap.registerPlugin(ScrollTrigger)

interface LenisProviderProps {
  children: ReactNode
}

export default function LenisProvider({ children }: LenisProviderProps) {
  const lenisRef = useRef<LenisRef>(null)

  useEffect(() => {
    function update(data: { timestamp: number }) {
      const time = data.timestamp
      lenisRef.current?.lenis?.raf(time)
    }

    frame.update(update, true)

    return () => cancelFrame(update)
  }, [])

  useEffect(() => {
    const lenis = lenisRef.current?.lenis

    if (lenis) {
      lenis.on('scroll', ScrollTrigger.update)

      ScrollTrigger.scrollerProxy(document.body, {
        scrollTop(value?: number) {
          if (value !== undefined) {
            lenis.scrollTo(value as number, { immediate: true })
          }
          return lenis.scroll
        },
        getBoundingClientRect() {
          return {
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight,
          }
        },
        pinType: 'transform',
      })

      setTimeout(() => {
        ScrollTrigger.refresh()
      }, 100)

      return () => {
        lenis.off('scroll', ScrollTrigger.update)
      }
    }
  }, [])

  return (
    <ReactLenis
      root
      ref={lenisRef}
      options={{
        autoRaf: false,
        lerp: 0.1,
        duration: 1.2,
        smoothWheel: true,
        syncTouch: false,
        touchMultiplier: 1,
        wheelMultiplier: 1,
      }}
    >
      {children}
    </ReactLenis>
  )
}
